import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

/**
 * Appointment Slice
 * Redux state management for appointment-related data.
 * Supports complete Doctor Workflow: Accept -> Start Visit -> Arrive -> Complete
 *
 * @author Civilhealth Development Team
 */

// Types
interface Appointment {
  id: number;
  bookingNumber: string;
  doctorId: number;
  doctorName: string;
  doctorSpecialization: string;
  doctorMobile?: string;
  officerId: number;
  officerName: string;
  officerMobile: string;
  officerDesignation: string;
  officerDepartment: string;
  officerAddress: string;
  officerEmail?: string;
  familyMemberId: number | null;
  familyMemberName: string | null;
  familyMemberRelationship: string | null;
  patientName: string;
  patientMobile: string;
  patientEmail?: string;
  patientAge: number | null;
  patientGender: string | null;
  appointmentDate: string;
  appointmentTime: string;
  startTime?: string;
  endTime: string;
  appointmentType: string;
  // New Status Flow: PENDING -> ACCEPTED -> IN_PROGRESS -> VISITED -> CLOSED
  status: 'PENDING' | 'ACCEPTED' | 'IN_PROGRESS' | 'VISITED' | 'CLOSED' | 'CANCELLED' | 'REJECTED' | 'NO_SHOW' | 'RESCHEDULED' | 'CONFIRMED' | 'DOCTOR_ASSIGNED' | 'DOCTOR_ON_THE_WAY';
  statusDisplayName: string;
  priority: string;
  reasonForVisit: string;
  symptoms: string;
  chiefComplaints: string;
  notes: string;
  specialNotes: string;
  fullVisitAddress: string;
  visitAddressLine1: string;
  visitAddressLine2: string;
  visitCity: string;
  visitState: string;
  visitPincode: string;
  visitLandmark: string;
  cancellationReason: string;
  doctorArrivalTime: string | null;
  doctorDepartureTime: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AppointmentState {
  appointments: Appointment[];
  currentAppointment: Appointment | null;
  loading: boolean;
  error: string | null;
  bookingSuccess: boolean;
  workflowLoading: boolean;
  workflowError: string | null;
}

// Initial state
const initialState: AppointmentState = {
  appointments: [],
  currentAppointment: null,
  loading: false,
  error: null,
  bookingSuccess: false,
  workflowLoading: false,
  workflowError: null,
};

// Async thunks
export const fetchAppointments = createAsyncThunk(
  'appointment/fetchAppointments',
  async (params: { page?: number; size?: number } | undefined, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', String(params?.page || 0));
      queryParams.append('size', String(params?.size || 50));

      const response = await api.get(`/appointments/my?${queryParams.toString()}`);
      const data = response.data.data;
      if (data.content && Array.isArray(data.content)) {
        return data.content;
      }
      return Array.isArray(data) ? data : [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch appointments');
    }
  }
);

export const fetchAppointmentById = createAsyncThunk(
  'appointment/fetchAppointmentById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/appointments/${id}`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch appointment');
    }
  }
);

export const bookAppointment = createAsyncThunk(
  'appointment/bookAppointment',
  async ({ officerId, data }: { officerId?: number; data: any }, { rejectWithValue }) => {
    try {
      const url = officerId
        ? `/appointments/book?officerId=${officerId}`
        : '/appointments/book';
      const response = await api.post(url, data);
      return response.data.data;
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Failed to book appointment';
      return rejectWithValue(message);
    }
  }
);

export const cancelAppointment = createAsyncThunk(
  'appointment/cancelAppointment',
  async ({ id, reason, cancelledBy }: { id: number; reason: string; cancelledBy: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/appointments/${id}/cancel?cancelledBy=${cancelledBy}`, { cancellationReason: reason });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel appointment');
    }
  }
);

export const confirmAppointment = createAsyncThunk(
  'appointment/confirmAppointment',
  async ({ id, confirmedBy }: { id: number; confirmedBy: number }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/appointments/${id}/confirm?confirmedBy=${confirmedBy}`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to confirm appointment');
    }
  }
);

// ==================== DOCTOR WORKFLOW THUNKS ====================

/**
 * Doctor accepts the appointment
 * Status: PENDING/CONFIRMED -> DOCTOR_ASSIGNED
 */
export const acceptAppointment = createAsyncThunk(
  'appointment/acceptAppointment',
  async (appointmentId: number, { rejectWithValue }) => {
    try {
      const response = await api.post(`/appointments/${appointmentId}/accept`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to accept appointment');
    }
  }
);

/**
 * Doctor starts visit (on the way to patient)
 * Status: DOCTOR_ASSIGNED -> DOCTOR_ON_THE_WAY
 */
export const startVisit = createAsyncThunk(
  'appointment/startVisit',
  async ({ appointmentId, notes }: { appointmentId: number; notes?: string }, { rejectWithValue }) => {
    try {
      const params = notes ? `?notes=${encodeURIComponent(notes)}` : '';
      const response = await api.post(`/appointments/${appointmentId}/start-visit${params}`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to start visit');
    }
  }
);

/**
 * Doctor arrives at location
 * Status: DOCTOR_ON_THE_WAY -> IN_PROGRESS
 */
export const arriveAtLocation = createAsyncThunk(
  'appointment/arriveAtLocation',
  async ({ appointmentId, latitude, longitude }: { appointmentId: number; latitude?: number; longitude?: number }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (latitude) params.append('latitude', String(latitude));
      if (longitude) params.append('longitude', String(longitude));
      const response = await api.post(`/appointments/${appointmentId}/arrive?${params.toString()}`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark arrival');
    }
  }
);

/**
 * Doctor completes examination and closes case
 * Status: IN_PROGRESS -> COMPLETED
 */
export const completeExamination = createAsyncThunk(
  'appointment/completeExamination',
  async ({ appointmentId, notes, visitDurationMinutes }: { appointmentId: number; notes?: string; visitDurationMinutes?: number }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (notes) params.append('notes', encodeURIComponent(notes));
      if (visitDurationMinutes) params.append('visitDurationMinutes', String(visitDurationMinutes));
      const response = await api.post(`/appointments/${appointmentId}/complete-examination?${params.toString()}`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to complete examination');
    }
  }
);

/**
 * Get doctor's today pending appointments (awaiting acceptance)
 */
export const fetchTodaysPendingAppointments = createAsyncThunk(
  'appointment/fetchTodaysPending',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/appointments/doctor/today-pending');
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch pending appointments');
    }
  }
);

/**
 * Get doctor's today accepted appointments (ready for visit)
 */
export const fetchTodaysAcceptedAppointments = createAsyncThunk(
  'appointment/fetchTodaysAccepted',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/appointments/doctor/today-accepted');
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch accepted appointments');
    }
  }
);

/**
 * Get doctor's in-progress appointments
 */
export const fetchInProgressAppointments = createAsyncThunk(
  'appointment/fetchInProgress',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/appointments/doctor/in-progress');
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch in-progress appointments');
    }
  }
);

/**
 * Get doctor's appointments filtered by status
 */
export const fetchDoctorAppointmentsByStatus = createAsyncThunk(
  'appointment/fetchDoctorByStatus',
  async (statuses: string[], { rejectWithValue }) => {
    try {
      const params = statuses.map(s => `status=${s}`).join('&');
      const response = await api.get(`/appointments/doctor/my?${params}`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch appointments');
    }
  }
);

// Slice
const appointmentSlice = createSlice({
  name: 'appointment',
  initialState,
  reducers: {
    clearAppointmentError: (state) => {
      state.error = null;
      state.workflowError = null;
    },
    clearCurrentAppointment: (state) => {
      state.currentAppointment = null;
    },
    resetBookingSuccess: (state) => {
      state.bookingSuccess = false;
    },
    clearWorkflowError: (state) => {
      state.workflowError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch appointments
      .addCase(fetchAppointments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAppointments.fulfilled, (state, action) => {
        state.loading = false;
        state.appointments = action.payload;
      })
      .addCase(fetchAppointments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch appointment by ID
      .addCase(fetchAppointmentById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAppointmentById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAppointment = action.payload;
      })
      .addCase(fetchAppointmentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Book appointment
      .addCase(bookAppointment.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.bookingSuccess = false;
      })
      .addCase(bookAppointment.fulfilled, (state, action) => {
        state.loading = false;
        state.appointments.push(action.payload);
        state.currentAppointment = action.payload;
        state.bookingSuccess = true;
      })
      .addCase(bookAppointment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.bookingSuccess = false;
      })
      // Cancel appointment
      .addCase(cancelAppointment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelAppointment.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.appointments.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.appointments[index] = action.payload;
        }
      })
      .addCase(cancelAppointment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Confirm appointment
      .addCase(confirmAppointment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(confirmAppointment.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.appointments.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.appointments[index] = action.payload;
        }
      })
      .addCase(confirmAppointment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // ==================== DOCTOR WORKFLOW ====================
      // Accept appointment
      .addCase(acceptAppointment.pending, (state) => {
        state.workflowLoading = true;
        state.workflowError = null;
      })
      .addCase(acceptAppointment.fulfilled, (state, action) => {
        state.workflowLoading = false;
        const index = state.appointments.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.appointments[index] = action.payload;
        }
        if (state.currentAppointment?.id === action.payload.id) {
          state.currentAppointment = action.payload;
        }
      })
      .addCase(acceptAppointment.rejected, (state, action) => {
        state.workflowLoading = false;
        state.workflowError = action.payload as string;
      })
      // Start visit
      .addCase(startVisit.pending, (state) => {
        state.workflowLoading = true;
        state.workflowError = null;
      })
      .addCase(startVisit.fulfilled, (state, action) => {
        state.workflowLoading = false;
        const index = state.appointments.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.appointments[index] = action.payload;
        }
        if (state.currentAppointment?.id === action.payload.id) {
          state.currentAppointment = action.payload;
        }
      })
      .addCase(startVisit.rejected, (state, action) => {
        state.workflowLoading = false;
        state.workflowError = action.payload as string;
      })
      // Arrive at location
      .addCase(arriveAtLocation.pending, (state) => {
        state.workflowLoading = true;
        state.workflowError = null;
      })
      .addCase(arriveAtLocation.fulfilled, (state, action) => {
        state.workflowLoading = false;
        const index = state.appointments.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.appointments[index] = action.payload;
        }
        if (state.currentAppointment?.id === action.payload.id) {
          state.currentAppointment = action.payload;
        }
      })
      .addCase(arriveAtLocation.rejected, (state, action) => {
        state.workflowLoading = false;
        state.workflowError = action.payload as string;
      })
      // Complete examination
      .addCase(completeExamination.pending, (state) => {
        state.workflowLoading = true;
        state.workflowError = null;
      })
      .addCase(completeExamination.fulfilled, (state, action) => {
        state.workflowLoading = false;
        const index = state.appointments.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.appointments[index] = action.payload;
        }
        if (state.currentAppointment?.id === action.payload.id) {
          state.currentAppointment = action.payload;
        }
      })
      .addCase(completeExamination.rejected, (state, action) => {
        state.workflowLoading = false;
        state.workflowError = action.payload as string;
      })
      // Fetch today's pending
      .addCase(fetchTodaysPendingAppointments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTodaysPendingAppointments.fulfilled, (state, action) => {
        state.loading = false;
        state.appointments = action.payload;
      })
      .addCase(fetchTodaysPendingAppointments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch today's accepted
      .addCase(fetchTodaysAcceptedAppointments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTodaysAcceptedAppointments.fulfilled, (state, action) => {
        state.loading = false;
        state.appointments = action.payload;
      })
      .addCase(fetchTodaysAcceptedAppointments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch in-progress
      .addCase(fetchInProgressAppointments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInProgressAppointments.fulfilled, (state, action) => {
        state.loading = false;
        state.appointments = action.payload;
      })
      .addCase(fetchInProgressAppointments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearAppointmentError, clearCurrentAppointment, resetBookingSuccess, clearWorkflowError } = appointmentSlice.actions;
export default appointmentSlice.reducer;